/**
 * Role-Based Access Control (RBAC) System for Palette Management
 * 
 * Defines roles, permissions, and access control utilities for shared palettes.
 * Supports offline-first, single-user mode with hooks for future multi-user scenarios.
 */

// Role definitions
export type PaletteRole = 'owner' | 'editor' | 'viewer';

// Permission types
export type Permission = 
  | 'palette:view'
  | 'palette:edit'
  | 'palette:delete'
  | 'palette:share'
  | 'palette:export'
  | 'palette:manage_permissions';

// Permission matrix: defines what each role can do
export const ROLE_PERMISSIONS: Record<PaletteRole, Permission[]> = {
  owner: [
    'palette:view',
    'palette:edit',
    'palette:delete',
    'palette:share',
    'palette:export',
    'palette:manage_permissions',
  ],
  editor: [
    'palette:view',
    'palette:edit',
    'palette:export',
  ],
  viewer: [
    'palette:view',
    'palette:export',
  ],
};

// Access control entry for a palette
export interface PaletteAccessControl {
  paletteId: string;
  ownerId: string; // User ID of the palette owner
  sharedWith?: Array<{
    userId: string;
    role: PaletteRole;
    grantedAt: number;
    grantedBy: string;
  }>;
  isPublic?: boolean; // If true, anyone can view (but not edit)
  publicRole?: 'viewer'; // Role for public access (currently only viewer supported)
}

/**
 * Check if a role has a specific permission
 */
export function hasPermission(role: PaletteRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role].includes(permission);
}

/**
 * Get all permissions for a role
 */
export function getRolePermissions(role: PaletteRole): Permission[] {
  return [...ROLE_PERMISSIONS[role]];
}

/**
 * Check if a user has permission to perform an action on a palette
 */
export function canPerformAction(
  userId: string,
  paletteAccess: PaletteAccessControl,
  permission: Permission
): boolean {
  // Owner always has all permissions
  if (paletteAccess.ownerId === userId) {
    return hasPermission('owner', permission);
  }

  // Check if user has explicit shared access
  const userAccess = paletteAccess.sharedWith?.find(
    (access) => access.userId === userId
  );
  
  if (userAccess) {
    return hasPermission(userAccess.role, permission);
  }

  // Check public access
  if (paletteAccess.isPublic && paletteAccess.publicRole) {
    return hasPermission(paletteAccess.publicRole, permission);
  }

  // No access by default
  return false;
}

/**
 * Get the effective role for a user on a palette
 */
export function getUserRole(
  userId: string,
  paletteAccess: PaletteAccessControl
): PaletteRole | null {
  // Owner check
  if (paletteAccess.ownerId === userId) {
    return 'owner';
  }

  // Explicit shared access
  const userAccess = paletteAccess.sharedWith?.find(
    (access) => access.userId === userId
  );
  
  if (userAccess) {
    return userAccess.role;
  }

  // Public access
  if (paletteAccess.isPublic && paletteAccess.publicRole) {
    return paletteAccess.publicRole;
  }

  return null;
}

/**
 * Validate if a role transition is allowed
 * Owners can change any role, editors cannot change roles
 */
export function canChangeRole(
  actorRole: PaletteRole,
  targetCurrentRole: PaletteRole,
  targetNewRole: PaletteRole
): boolean {
  // Only owners can manage permissions
  if (actorRole !== 'owner') {
    return false;
  }

  // Cannot demote yourself from owner (must transfer ownership first)
  if (targetCurrentRole === 'owner' && targetNewRole !== 'owner') {
    return false;
  }

  return true;
}

/**
 * Get human-readable role description
 */
export function getRoleDescription(role: PaletteRole): string {
  const descriptions: Record<PaletteRole, string> = {
    owner: 'Full control including deletion and permission management',
    editor: 'Can view and modify palette colors and properties',
    viewer: 'Can view and export palette but cannot make changes',
  };
  return descriptions[role];
}

/**
 * Get human-readable permission description
 */
export function getPermissionDescription(permission: Permission): string {
  const descriptions: Record<Permission, string> = {
    'palette:view': 'View palette details and colors',
    'palette:edit': 'Modify palette colors, name, and tags',
    'palette:delete': 'Delete the palette permanently',
    'palette:share': 'Share palette with other users',
    'palette:export': 'Export palette in various formats',
    'palette:manage_permissions': 'Change user roles and access',
  };
  return descriptions[permission];
}

/**
 * Create default access control for a new palette
 */
export function createDefaultAccessControl(
  paletteId: string,
  ownerId: string
): PaletteAccessControl {
  return {
    paletteId,
    ownerId,
    sharedWith: [],
    isPublic: false,
  };
}

/**
 * Share palette with a user
 */
export function sharePalette(
  accessControl: PaletteAccessControl,
  userId: string,
  role: PaletteRole,
  grantedBy: string
): PaletteAccessControl {
  const sharedWith = accessControl.sharedWith || [];
  
  // Remove existing access if present
  const filtered = sharedWith.filter((access) => access.userId !== userId);
  
  // Add new access
  filtered.push({
    userId,
    role,
    grantedAt: Date.now(),
    grantedBy,
  });

  return {
    ...accessControl,
    sharedWith: filtered,
  };
}

/**
 * Revoke palette access from a user
 */
export function revokePaletteAccess(
  accessControl: PaletteAccessControl,
  userId: string
): PaletteAccessControl {
  return {
    ...accessControl,
    sharedWith: (accessControl.sharedWith || []).filter(
      (access) => access.userId !== userId
    ),
  };
}

/**
 * Make palette public or private
 */
export function setPublicAccess(
  accessControl: PaletteAccessControl,
  isPublic: boolean,
  publicRole: 'viewer' = 'viewer'
): PaletteAccessControl {
  return {
    ...accessControl,
    isPublic,
    publicRole: isPublic ? publicRole : undefined,
  };
}

/**
 * Transfer ownership to another user
 */
export function transferOwnership(
  accessControl: PaletteAccessControl,
  newOwnerId: string,
  currentOwnerId: string
): PaletteAccessControl {
  const sharedWith = accessControl.sharedWith || [];
  
  // Remove new owner from shared list if present
  const filtered = sharedWith.filter((access) => access.userId !== newOwnerId);
  
  // Add previous owner as editor
  filtered.push({
    userId: currentOwnerId,
    role: 'editor',
    grantedAt: Date.now(),
    grantedBy: newOwnerId,
  });

  return {
    ...accessControl,
    ownerId: newOwnerId,
    sharedWith: filtered,
  };
}
